import { API_BASE, AUTH_TOKEN } from '../config';

// World objects — anything with a saved placement, stored in the DB and edited
// live. `zone` is where it stands: 'street' for the open city, a ROOMS key for
// an interior. One path covers both.
//
// The old editors nudged local state and asked you to paste coordinates into
// rooms.js. That makes an arrangement a DEPLOY, and nobody arranges a room
// through a deploy. These read and write `world_room_objects` (migration 261),
// so moving something is the save.
//
// Reads are public — the world renders these for everyone. Writes are admin.
const hdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` });

/** Everything placed in one room. Returns [] pre-migration rather than throwing. */
export async function listWorldObjects(zoneId, kind = null) {
  try {
    // Zone is OPTIONAL. Portals need every zone at once: an exit standing on the
    // subway platform points at an entrance standing on the street, and it has to
    // resolve that target while you are underground and the street is not loaded.
    const q = new URLSearchParams({ ...(zoneId ? { zone: zoneId } : {}), ...(kind ? { kind } : {}) });
    const res = await fetch(`${API_BASE}/api/world/objects?${q}`);
    const d = await res.json().catch(() => ({}));
    return d.objects || [];
  } catch { return []; }
}

/**
 * One BLOCK of the street — every object within `r` cells of (cx, cz).
 *
 * The street level of Planet v0.2 §10's descent. `listWorldObjects` asks for a
 * whole zone, which is right for an interior (you are standing in it, it is
 * small, you need all of it) and wrong for a city: it ships every building and
 * every resident in the world to a client that can see one street of them.
 *
 * Returns the server's `cellSize` alongside, so the client never has to hardcode
 * a copy of it — the grid the API files objects into is the grid the client asks
 * in, by construction rather than by both being set to 32.
 */
export async function listWorldBlock(district, cx, cz, r, zone = 'street') {
  try {
    const q = new URLSearchParams({ district, cx: String(cx), cz: String(cz), r: String(r) });
    if (zone) q.set('zone', zone);
    const res = await fetch(`${API_BASE}/api/world/objects?${q}`);
    const d = await res.json().catch(() => ({}));
    return { objects: d.objects || [], epoch: d.epoch || '', cellSize: d.cellSize || 32 };
  } catch { return { objects: [], epoch: '', cellSize: 32 }; }
}

/**
 * A room's saved settings — fit, spawn, walk bounds. One row per room
 * (kind 'room_config'), so tuning a room is a save rather than a deploy.
 * Returns null when the room has never been tuned, and the caller falls back to
 * whatever rooms.js authored.
 */
export async function loadRoomConfig(roomId) {
  const rows = await listWorldObjects(roomId, 'room_config');
  const row = rows?.[0];
  return row ? { objectId: row.object_id, ...(row.data || {}) } : null;
}

/** Write it, creating the row on first save. */
export async function saveRoomConfig(roomId, objectId, cfg) {
  const body = { zone_id: roomId, kind: 'room_config', ref: null, pos: [0, 0, 0], data: cfg };
  const saved = objectId ? await updateWorldObject(objectId, { data: cfg }) : await createWorldObject(body);
  if (!saved?.data) throw new Error('the API dropped `data` — restart it');
  return saved;
}

export async function createWorldObject(body) {
  const res = await fetch(`${API_BASE}/api/world/objects`, { method: 'POST', headers: hdr(), body: JSON.stringify(body) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `save failed (${res.status})`);
  return d.object;
}

export async function updateWorldObject(id, body) {
  const res = await fetch(`${API_BASE}/api/world/objects/${id}`, { method: 'PATCH', headers: hdr(), body: JSON.stringify(body) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `save failed (${res.status})`);
  return d.object;
}

export async function deleteWorldObject(id) {
  const res = await fetch(`${API_BASE}/api/world/objects/${id}`, { method: 'DELETE', headers: hdr() });
  if (!res.ok) throw new Error(`delete failed (${res.status})`);
  return true;
}

// ── Camera basis ──────────────────────────────────────────────────────────
// "Left" has to mean left FROM WHERE YOU STAND, or nothing lands where you
// expect in a room you are looking around inside. The panel is DOM, outside the
// Canvas, so it cannot read the camera itself — a probe inside the scene keeps
// this up to date and the panel reads it when you press a button.
//
// A plain module object rather than store state on purpose: it changes every
// frame and nothing should re-render because you turned your head.
export const camBasis = { fx: 0, fz: -1, rx: 1, rz: 0, px: 0, py: 0, pz: 0 };

// Position as well as direction. "Left" needed the direction; "put it next to
// me" needs both — a body placed at the room's spawn is not next to you once
// you have walked anywhere, and in a big room it can be out of sight entirely.
export function setCamPos(p) { camBasis.px = p.x; camBasis.py = p.y; camBasis.pz = p.z; }

export function setCamBasis(dir) {
  const x = dir.x, z = dir.z;
  const len = Math.hypot(x, z);
  if (len < 1e-6) return;
  camBasis.fx = x / len; camBasis.fz = z / len;
  camBasis.rx = -camBasis.fz; camBasis.rz = camBasis.fx;
}
